import { getAuth, User } from 'firebase/auth';

import { getIdTokenWhenReady } from '../../Components/RoleSwitcher/roleContext';
import { getUserProfile } from '../firebase/users';

const NOVA_URL = import.meta.env.VITE_WS_MARKETPLACE_APP_URL || '';
const RETRY_DELAY_MS = 3000;
const MAX_TRIES = 3;

const ROLE_TO_CLAIM: Record<string, string> = {
  director: 'organizationAdmin',
  location_admin: 'locationAdmin',
  teacher: 'teacher',
  support_staff: 'supportStaff',
  parent: 'parent',
  guardian: 'guardian',
  emergency_contact: 'emergencyContact',
};

export interface SwitchedMembership {
  id: string;
  identity_id: string;
  context_type: string;
  context_id: string;
  role: string;
}

/** Nova's `CCMSWeb.MembershipSwitchJSON.switch_response/2`. */
interface SwitchResponse {
  active: SwitchedMembership;
  previous_context: SwitchedMembership | null;
  switch_version: number;
  firestore_org_id: string | null;
  org_projection: 'projected' | `skipped_${string}` | null;
  org_pending: boolean;
  force_token_refresh: boolean;
  claims_pending?: true;
}

type HttpFailure = { error?: string; httpStatus: number; body: string };

export type SwitchResult =
  | { status: 'ok'; active: SwitchedMembership }
  | { status: 'unavailable'; reason: string; active: SwitchedMembership }
  | {
      status: 'not_ready';
      reason: 'pending' | 'claims' | 'org';
      expectedClaim?: string;
      active: SwitchedMembership;
    }
  | ({ status: 'not_found' } & HttpFailure)
  | ({ status: 'error' } & HttpFailure)
  | { status: 'superseded' }
  | { status: 'signed_out' };

const wait = () => new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS));

// A parent or family switch is skipped_not_provider: no school to project, but nothing missing.
const isTerminalSkip = (projection: SwitchResponse['org_projection']): projection is `skipped_${string}` =>
  !!projection && projection !== 'projected' && projection !== 'skipped_not_provider';

const parseError = (body: string): string | undefined => {
  try {
    return JSON.parse(body).error;
  } catch {
    return undefined;
  }
};

const hasClaim = async (user: User, claim: string) => (await user.getIdTokenResult()).claims[claim] === true;

const activeMembershipId = async (): Promise<string | null | undefined> => {
  const token = await getIdTokenWhenReady();
  const res = await fetch(`${NOVA_URL}/firebase-api/memberships`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) return undefined;
  return (await res.json()).active_membership_id;
};

type PostFailure = Extract<SwitchResult, { status: 'not_found' | 'error' | 'signed_out' }>;

const isFailure = (value: SwitchResponse | PostFailure): value is PostFailure => 'status' in value;

const post = async (membershipId: string): Promise<SwitchResponse | PostFailure> => {
  const token = await getIdTokenWhenReady();
  if (!token) return { status: 'signed_out' };

  const res = await fetch(`${NOVA_URL}/firebase-api/memberships/switch-context`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ membership_id: membershipId }),
  });

  if (!res.ok) {
    const body = await res.text().catch(() => '');
    return {
      status: res.status === 404 ? 'not_found' : 'error',
      error: parseError(body),
      httpStatus: res.status,
      body,
    };
  }

  return res.json();
};

export async function switchContext(
  membershipId: string
): Promise<PostFailure | { status: 'ok'; active: SwitchedMembership }> {
  const data = await post(membershipId);
  if (isFailure(data)) return data;

  const user = getAuth().currentUser;
  if (!user) return { status: 'signed_out' };

  await user.getIdToken(true);
  return { status: 'ok', active: data.active };
}

export async function switchContextAndVerify(membershipId: string): Promise<SwitchResult> {
  let data = await post(membershipId);
  for (let attempt = 1; attempt < MAX_TRIES && !isFailure(data) && data.org_pending; attempt++) {
    await wait();
    const current = await activeMembershipId();
    if (current === undefined) break;
    if (current !== membershipId) {
      await getAuth().currentUser?.getIdToken(true);
      return { status: 'superseded' };
    }
    data = await post(membershipId);
  }
  if (isFailure(data)) return data;

  const { active, org_projection: projection } = data;
  if (data.claims_pending && !data.org_pending) await wait();

  const user = getAuth().currentUser;
  if (!user) return { status: 'signed_out' };

  await user.getIdToken(true);
  if (data.org_pending) return { status: 'not_ready', reason: 'pending', active };

  const expectedClaim = ROLE_TO_CLAIM[active?.role];
  if (expectedClaim && !(await hasClaim(user, expectedClaim))) {
    // New custom claims can lag the next token mint by a few seconds.
    await wait();
    await user.getIdToken(true);
    if (!(await hasClaim(user, expectedClaim))) return { status: 'not_ready', reason: 'claims', expectedClaim, active };
  }

  // A terminal skip means Nova wrote no currentOrganization, so there is nothing to compare.
  if (isTerminalSkip(projection)) return { status: 'unavailable', reason: projection, active };

  if (projection === 'projected') {
    const orgMatches = async () =>
      (await getUserProfile(user.uid).catch(() => null))?.currentOrganization === data.firestore_org_id;
    if (!(await orgMatches())) {
      await wait();
      if (!(await orgMatches())) return { status: 'not_ready', reason: 'org', active };
    }
  }

  return { status: 'ok', active };
}
